import { Color } from "@repo/app-types/constants/colors";
import { ToolUsageAccuracyStatus } from "@repo/app-types/entities/toolUsageAccuracy";
import { Size } from "@repo/ui/helpers/helpers";
import { pillStyle, type PillColor } from "@repo/ui/Pills/pill";
import {
  IconCircleCheck,
  IconCircleMinus,
  IconCircleX,
  IconExclamationCircle,
} from "@tabler/icons-react";
import type { FC } from "react";
import { Button } from "react-aria-components";

export interface ToolUsageAccuracyStatusPillProps {
  status: ToolUsageAccuracyStatus;
  size?: Size;
  onClick?: () => void;
}

const iconComponent: Record<ToolUsageAccuracyStatus, typeof IconCircleCheck> = {
  [ToolUsageAccuracyStatus.UsedCorrect]: IconCircleCheck,
  [ToolUsageAccuracyStatus.UnusedCorrect]: IconCircleMinus,
  [ToolUsageAccuracyStatus.UsedIncorrect]: IconCircleX,
  [ToolUsageAccuracyStatus.UsedFailed]: IconExclamationCircle,
  [ToolUsageAccuracyStatus.UnusedIncorrect]: IconCircleX,
};

const pillColor: Record<ToolUsageAccuracyStatus, PillColor> = {
  [ToolUsageAccuracyStatus.UsedCorrect]: Color.Green,
  [ToolUsageAccuracyStatus.UnusedCorrect]: Color.Gray,
  [ToolUsageAccuracyStatus.UsedIncorrect]: Color.Red,
  [ToolUsageAccuracyStatus.UsedFailed]: Color.Amber,
  [ToolUsageAccuracyStatus.UnusedIncorrect]: Color.Red,
};

const statusLabel: Record<ToolUsageAccuracyStatus, string> = {
  [ToolUsageAccuracyStatus.UsedCorrect]: "Used correctly",
  [ToolUsageAccuracyStatus.UnusedCorrect]: "N/A",
  [ToolUsageAccuracyStatus.UsedIncorrect]: "Used incorrectly",
  [ToolUsageAccuracyStatus.UsedFailed]: "Used with error",
  [ToolUsageAccuracyStatus.UnusedIncorrect]: "Failed to use",
};

export const ToolUsageAccuracyStatusPill: FC<
  ToolUsageAccuracyStatusPillProps
> = ({ status, size = Size.SMALL, onClick }) => {
  const Icon = iconComponent[status];
  const color = pillColor[status];
  const label = statusLabel[status];

  const content = (
    <>
      <Icon className="size-4 shrink-0" />
      {label}
    </>
  );

  if (onClick) {
    return (
      <Button
        className={pillStyle({
          size,
          color,
          isClickable: true,
          class: "gap-1",
        })}
        onPress={onClick}
        type="button"
      >
        {content}
      </Button>
    );
  }

  return (
    <div className={pillStyle({ size, color, class: "gap-1" })}>{content}</div>
  );
};
